import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { login as apiLogin, getMe } from '../services/api';

interface ModuleAccess {
  module: string;
  can_read: boolean;
  can_write: boolean;
}

interface User {
  id: string;
  nom: string;
  prenom: string;
  prenomnom: string;
  email: string;
  matricule: string;
  telephone?: string;
  role: string;
  direction?: string;
  direction_id?: string;
  service?: string;
  modules?: ModuleAccess[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (loginId: string, password: string) => Promise<void>;
  logout: () => void;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  hasModuleAccess: (moduleName: string) => boolean;
  hasModuleWriteAccess: (moduleName: string) => boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem('chamfb_token');
    const savedUser = localStorage.getItem('chamfb_user');
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = async (loginId: string, password: string) => {
    const response = await apiLogin(loginId, password);
    const { token: newToken, user: newUser } = response.data;
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('chamfb_token', newToken);
    localStorage.setItem('chamfb_user', JSON.stringify(newUser));
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('chamfb_token');
    localStorage.removeItem('chamfb_user');
  };

  const isAdmin = () => {
    return !!user && ['superadmin', 'admin', 'bureau', 'admin_direction'].includes(user.role);
  };

  const isSuperAdmin = () => {
    return !!user && user.role === 'superadmin';
  };

  const hasModuleAccess = (moduleName: string): boolean => {
    if (!user) return false;
    if (['superadmin', 'admin', 'bureau'].includes(user.role)) return true;
    if (user.role === 'admin_direction' && user.modules) {
      return user.modules.some(m => m.module === moduleName && m.can_read);
    }
    return false;
  };

  const hasModuleWriteAccess = (moduleName: string): boolean => {
    if (!user) return false;
    if (['superadmin', 'admin', 'bureau'].includes(user.role)) return true;
    if (user.role === 'admin_direction' && user.modules) {
      return user.modules.some(m => m.module === moduleName && m.can_write);
    }
    return false;
  };

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated: !!token, loading, login, logout, isAdmin, isSuperAdmin, hasModuleAccess, hasModuleWriteAccess }}>
      {children}
    </AuthContext.Provider>
  );
};
